import type * as React from 'react';
import {useMemo} from 'react';

const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
svg.style.width = '0';
svg.style.height = '0';
svg.style.position = 'absolute';
svg.style.top = '-100%';
svg.style.left = '-100%';

const textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text');
svg.appendChild(textElement);
document.body.appendChild(svg);

const getStringWidth = (input: string, fontSize: number) => {
    textElement.style.fontSize = `${fontSize}px`;
    textElement.textContent = input;
    return textElement.getComputedTextLength();
};

export type HeaderBlock = {
    style: 'header';
    content: string;
};
export type TextBlock = HeaderBlock | string;

type Props = {
    blocks: TextBlock[];
    width: number;
    fontSize: number;
    fontWeight?: string | number;
    x?: number;
    y?: number;
    fill?: string;
    textAnchor?: string;
};

type Line = {
    style: 'default' | 'header';
    words: string[];
};

const SvgText: React.FC<Props> = ({
    blocks,
    width,
    fontSize,
    fontWeight,
    x,
    y,
    fill,
    textAnchor,
}: Props) => {
    const lines = useMemo(() => {
        const spaceWidth = getStringWidth('\u00A0', fontSize);

        return blocks.reduce<Line[]>((lines, block) => {
            const style = typeof block === 'string' ? 'default' : block.style;
            const content = typeof block === 'string' ? block : block.content;

            return content.split('\n').reduce((_contentLines, line) => {
                const words = line.split(/\s+/).map(word => ({
                    word,
                    length: getStringWidth(word, fontSize),
                }));

                if (words.length === 1 && words[0].length === 0) {
                    lines.push({style, words: [' ']});
                    return lines;
                }

                let currentLine: Line | null = null;
                let currentLength = 0;

                for (const word of words) {
                    if (currentLine && currentLength + spaceWidth + word.length <= width) {
                        currentLine.words.push(word.word);
                        currentLength += spaceWidth + word.length;
                        continue;
                    }

                    currentLine = {style, words: [word.word]};
                    lines.push(currentLine);
                    currentLength = word.length;
                }

                return lines;
            }, lines);
        }, []);
    }, [blocks, fontSize, width]);

    return (
        <text
            fontSize={fontSize}
            fontWeight={fontWeight}
            textAnchor={textAnchor}
            x={x}
            y={y}
            fill={fill}
        >
            {lines.map((line, index) => (
                <tspan
                    key={`${line.words.join('-')}-${index}`}
                    x={x}
                    dy='1em'
                    fontWeight={line.style === 'default' ? undefined : 'bold'}
                >
                    {line.words.join(' ')}
                </tspan>
            ))}
        </text>
    );
};

export default SvgText;
